<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import FilterConditionPill from './FilterConditionPill.vue';
import { payloadToPills, pillsToPayload, withAndOnly } from './pillsPayload';

// UI-FILTROS-01 (§2.3, D4, D6): o filtro avançado de conversas e de contatos em pílulas, no
// lugar do montador de condições (ConversationFilter e ContactsFilter). Parte do payload de hoje
// e devolve o payload de hoje: quem usa segue com filterQueryGenerator, POST /filter e
// custom_filters.query como antes.
const props = defineProps({
  // Os atributos no formato do provider (provider.js, contactProvider.js).
  filterTypes: { type: Array, required: true },
  // O filtro de onde se parte, no formato de hoje (camelCase).
  payload: { type: Array, default: () => [] },
  // Os atributos à vista; o resto mora em "Mais filtros" até ganhar valor.
  primary: { type: Array, default: () => [] },
  title: { type: String, default: '' },
  // Editando uma visão salva (pasta, segmento): o nome e o botão de atualizar.
  isView: { type: Boolean, default: false },
  name: { type: String, default: '' },
  nameLabel: { type: String, default: '' },
  updateLabel: { type: String, default: '' },
});

const emit = defineEmits(['apply', 'update', 'close', 'reset']);

const { t } = useI18n();

const pills = ref([]);
const representavel = ref(true);
const motivo = ref(null);
// O payload que virou as pílulas: o de fora, ou ele convertido para E.
const origem = ref([]);
const nome = ref(props.name);
watch(
  () => props.name,
  valor => {
    nome.value = valor;
  }
);
let editado = false;

// Os atributos que vieram com pílula ficam à vista, mesmo sem valor. Trocar o operador de um
// extra que veio preenchido (pasta, segmento, filtro aplicado) zera o valor, e a pílula sumiria
// com a lista aberta, levando a condição do rascunho sem aviso. O extra aberto em "Mais filtros"
// a FilterBar já guarda.
const vistos = ref([]);
const marcarVistos = lista => {
  const novos = lista
    .map(pill => pill.attributeKey)
    .filter(chave => !vistos.value.includes(chave));
  if (novos.length) vistos.value = [...vistos.value, ...novos];
};

const iniciar = payload => {
  const resultado = payloadToPills(payload, props.filterTypes);
  origem.value = payload;
  pills.value = resultado.pills;
  marcarVistos(resultado.pills);
  representavel.value = resultado.representavel;
  motivo.value = resultado.motivo;
};

watch(
  () => props.payload,
  payload => {
    editado = false;
    iniciar(payload);
  },
  { immediate: true }
);
// Os atributos personalizados podem chegar depois: sem eles, a pílula do personalizado sairia
// como atributo desconhecido. Enquanto ninguém mexeu, lê de novo.
watch(
  () => props.filterTypes.map(tipo => tipo.attributeKey).join(),
  () => {
    if (!editado) iniciar(origem.value);
  }
);

const tipoDe = chave =>
  props.filterTypes.find(tipo => tipo.attributeKey === chave);

const campos = computed(() =>
  props.filterTypes.map(tipo => ({
    key: tipo.attributeKey,
    label: tipo.attributeName,
    scalar: true,
    extra: !props.primary.includes(tipo.attributeKey),
  }))
);

// A barra guarda uma pílula por atributo; a ordem que vale é a dos atributos.
const modelo = computed(() =>
  Object.fromEntries(pills.value.map(pill => [pill.attributeKey, pill]))
);
const mudarModelo = novo => {
  editado = true;
  pills.value = props.filterTypes
    .map(tipo => novo[tipo.attributeKey])
    .filter(Boolean);
};

// Zera o rascunho; o evento reset serve a quem também tira o filtro aplicado (contatos).
const limpar = () => {
  editado = true;
  origem.value = [];
  pills.value = [];
  representavel.value = true;
  motivo.value = null;
  emit('reset');
};

// Na leitura, o atributo que a conta não tem mais aparece pelo nome da chave.
const tipoParaLeitura = pill =>
  tipoDe(pill.attributeKey) ?? {
    attributeKey: pill.attributeKey,
    attributeName: pill.attributeKey,
    filterOperators: [],
    options: [],
  };

const ligacao = indice =>
  String(origem.value[indice]?.queryOperator || 'and').toLowerCase() === 'or'
    ? t('FILTER.QUERY_DROPDOWN_LABELS.OR')
    : t('FILTER.QUERY_DROPDOWN_LABELS.AND');

const aviso = computed(
  () =>
    ({
      or: t('FILTER_BAR.ADVANCED.READ_ONLY.OR'),
      attribute: t('FILTER_BAR.ADVANCED.READ_ONLY.ATTRIBUTE'),
      operator: t('FILTER_BAR.ADVANCED.READ_ONLY.OPERATOR'),
      repeated: t('FILTER_BAR.ADVANCED.READ_ONLY.REPEATED'),
    })[motivo.value] ?? ''
);

const dialogoRef = ref(null);
const converter = () => {
  editado = true;
  iniciar(withAndOnly(origem.value));
  dialogoRef.value?.close();
};

const podeAplicar = computed(
  () => representavel.value && pills.value.length > 0
);
const podeAtualizar = computed(
  () => podeAplicar.value && Boolean(nome.value.trim())
);

const aplicar = () => {
  if (!podeAplicar.value) return;
  emit('apply', pillsToPayload(pills.value, props.filterTypes));
};
const atualizar = () => {
  if (!podeAtualizar.value) return;
  emit('update', pillsToPayload(pills.value, props.filterTypes), nome.value);
};

// Como o montador antigo: sair de cena (aplicou, trocou de tela) também fecha, e quem usa limpa
// o rascunho.
onBeforeUnmount(() => emit('close'));
</script>

<template>
  <div class="flex flex-col gap-3">
    <h3 v-if="title" class="text-sm font-medium text-n-slate-12">
      {{ title }}
    </h3>
    <FilterBar
      v-if="representavel"
      :model-value="modelo"
      :fields="campos"
      :revealed="vistos"
      :search="false"
      @update:model-value="mudarModelo"
      @reset="limpar"
    >
      <template
        v-for="tipo in filterTypes"
        :key="tipo.attributeKey"
        #[`pill-${tipo.attributeKey}`]="{ value, update }"
      >
        <FilterConditionPill
          :filter-type="tipo"
          :model-value="value[0] ?? null"
          @update:model-value="pill => update(pill ? [pill] : [])"
        />
      </template>
    </FilterBar>
    <template v-else>
      <p class="text-sm text-n-amber-11">{{ aviso }}</p>
      <div class="flex flex-wrap items-center gap-2">
        <template v-for="(pill, indice) in pills" :key="indice">
          <span v-if="indice > 0" class="text-xs text-n-slate-11">
            {{ ligacao(indice - 1) }}
          </span>
          <FilterConditionPill
            :filter-type="tipoParaLeitura(pill)"
            :model-value="pill"
            disabled
            :disabled-hint="t('FILTER_BAR.ADVANCED.READ_ONLY.HINT')"
          />
        </template>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          v-if="motivo === 'or'"
          size="sm"
          variant="faded"
          :label="t('FILTER_BAR.ADVANCED.CONVERT')"
          @click="dialogoRef?.open()"
        />
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          :label="t('FILTER_BAR.CLEAR')"
          @click="limpar"
        />
      </div>
    </template>
    <div class="flex flex-wrap items-center justify-end gap-2">
      <input
        v-if="isView"
        v-model="nome"
        type="text"
        class="flex-1 h-8 min-w-40 px-3 text-sm rounded-lg outline-none reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-1 focus:ring-n-brand"
        :placeholder="nameLabel"
        :aria-label="nameLabel"
      />
      <Button
        size="sm"
        variant="faded"
        color="slate"
        :label="t('FILTER_BAR.ADVANCED.CANCEL')"
        @click="emit('close')"
      />
      <Button
        v-if="isView"
        size="sm"
        :label="updateLabel"
        :disabled="!podeAtualizar"
        @click="atualizar"
      />
      <Button
        v-else
        size="sm"
        :label="t('FILTER_BAR.ADVANCED.APPLY')"
        :disabled="!podeAplicar"
        @click="aplicar"
      />
    </div>
    <Dialog
      ref="dialogoRef"
      :title="t('FILTER_BAR.ADVANCED.CONVERT_TITLE')"
      :description="t('FILTER_BAR.ADVANCED.CONVERT_DESCRIPTION')"
      :confirm-button-label="t('FILTER_BAR.ADVANCED.CONVERT_CONFIRM')"
      @confirm="converter"
    />
  </div>
</template>
